// Ollama models page (roadmap item 38).
//
// Mounted at `/repos/:id/ollama` for any repository whose `format` is
// `ollama`. Shows the catalog of (model, tag) pairs the repo holds and,
// for the model the operator clicks, the per-layer breakdown — License,
// Modelfile, Template, System prompt, model weights — that's the part of
// an Ollama bundle that matters for compliance review.
//
// The page derives the (project_slug, repo_slug) pair from the loaded
// `Repo` (`useRepoDetail`) plus the cached `Project` list (`useProjects`)
// rather than threading slugs through the URL — the existing `/repos/:id`
// pattern is UUID-keyed, and this keeps that surface stable.

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Box, Copy, FileText, Layers, ScrollText, Wand2 } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useProjects, useRepoDetail } from '../../api/client';
import {
  type OllamaLayer,
  type OllamaModelEntry,
  useOllamaModelDetail,
  useOllamaModels,
} from '../../api/ollama';

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// shortHex trims a long sha256 down to the leading characters that fit
// in a table cell while still being recognisably one digest. We never
// strip the `sha256:` prefix here so a copy-paste lands in code that
// expects the canonical OCI digest shape.
function shortHex(d?: string): string {
  if (!d) return '';
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

// Ollama-specific layer media types, mapped to the human label and the
// icon the catalog UI renders. Falls back to "Layer" for anything we
// don't recognise — there's no harm in surfacing arbitrary media types,
// and forward-compatibility for new Ollama bundle parts comes for free.
const LAYER_KIND_BY_MEDIA: Record<string, { label: string; icon: typeof Box }> = {
  'application/vnd.ollama.image.model': { label: 'Model weights', icon: Box },
  'application/vnd.ollama.image.modelfile': { label: 'Modelfile', icon: FileText },
  'application/vnd.ollama.image.template': { label: 'Prompt template', icon: Wand2 },
  'application/vnd.ollama.image.system': { label: 'System prompt', icon: ScrollText },
  'application/vnd.ollama.image.license': { label: 'License', icon: ScrollText },
  'application/vnd.ollama.image.params': { label: 'Params', icon: Layers },
};

function modelsCountLabel(models?: { models: unknown[] }): string | undefined {
  if (!models) return undefined;
  return `${models.models.length} model${models.models.length === 1 ? '' : 's'}`;
}

function layerKindLabel(mediaType: string): { label: string; icon: typeof Box } {
  return LAYER_KIND_BY_MEDIA[mediaType] ?? { label: 'Layer', icon: Layers };
}

export function OllamaModels() {
  const { id } = useParams<{ id: string }>();
  const { data: repo, isLoading: repoLoading, error: repoError } = useRepoDetail(id);
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );
  const { data: models, error: modelsError, isLoading: modelsLoading } = useOllamaModels(
    projectSlug,
    repo?.slug,
  );
  const [selected, setSelected] = useState<OllamaModelEntry | null>(null);
  const detail = useOllamaModelDetail(
    projectSlug,
    repo?.slug,
    selected?.model,
    selected?.tag,
  );

  if (repoLoading) {
    return <div className="p-6 text-fg-muted">Loading repository…</div>;
  }
  if (repoError || !repo) {
    return (
      <div className="p-6 text-danger">
        Failed to load repository: {String(repoError ?? 'unknown')}
      </div>
    );
  }
  if (repo.format !== 'ollama') {
    return (
      <div className="p-6">
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to repository
        </Link>
        <p className="text-fg-muted">
          This page is only available for repositories of format{' '}
          <code className="orb-code">ollama</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {repo.name}
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">Ollama models</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          {repo.name}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          Models pushed to this Ollama-format repository. Each row shows the
          tag, the manifest digest, and the total on-disk size of all
          referenced layers. Click a row for the per-layer breakdown.
        </p>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Box className="h-4 w-4 text-accent" />
              Catalog
            </span>
          }
          description={modelsCountLabel(models)}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">Model</th>
                <th className="px-4 py-2 font-medium">Tag</th>
                <th className="px-4 py-2 font-medium">Layers</th>
                <th className="px-4 py-2 font-medium">Total size</th>
                <th className="px-4 py-2 font-medium">Manifest digest</th>
                <th className="px-4 py-2 font-medium">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {modelsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    Loading…
                  </td>
                </tr>
              )}
              {modelsError && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={6}>
                    {String(modelsError)}
                  </td>
                </tr>
              )}
              {models?.models.length === 0 && !modelsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    No models pushed yet. Point the Ollama CLI at this repo
                    with{' '}
                    <code className="orb-code">
                      OLLAMA_HOST={'<host>'}/ollama/{projectSlug}/{repo.slug}
                    </code>{' '}
                    and run <code className="orb-code">ollama push</code>.
                  </td>
                </tr>
              )}
              {models?.models.map((m) => {
                const isSelected =
                  selected?.model === m.model && selected?.tag === m.tag;
                return (
                  <tr
                    key={`${m.model}:${m.tag}`}
                    className={`border-b border-border/50 cursor-pointer ${
                      isSelected ? 'bg-bg-hover/60' : 'hover:bg-bg-hover/40'
                    }`}
                    onClick={() => setSelected(m)}
                  >
                    <td className="px-4 py-2 font-medium text-fg">{m.model}</td>
                    <td className="px-4 py-2">
                      <Badge tone="accent">{m.tag}</Badge>
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {m.layer_count}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {bytes(m.total_size_bytes)}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-fg-muted">
                      {shortHex(m.digest)}
                    </td>
                    <td className="px-4 py-2 text-fg-muted">
                      {new Date(m.uploaded_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {models && (
        <Card>
          <CardHeader title="How to pull" />
          <CardBody>
            <PullSnippet hint={models.pull_hint} />
          </CardBody>
        </Card>
      )}

      {selected && (
        <Card>
          <CardHeader
            title={
              <span className="inline-flex items-center gap-2">
                <Layers className="h-4 w-4 text-accent" />
                {selected.model}
                <Badge tone="accent">{selected.tag}</Badge>
              </span>
            }
            description="Manifest layers — License + Modelfile carry the compliance-relevant metadata."
          />
          <CardBody>
            {detail.isLoading && <p className="text-fg-muted">Loading detail…</p>}
            {detail.error && (
              <p className="text-danger">{String(detail.error)}</p>
            )}
            {detail.data && <ModelDetail detail={detail.data} />}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

interface PullSnippetProps {
  readonly hint: string;
}

function PullSnippet({ hint }: PullSnippetProps) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(hint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail in non-secure contexts; the operator can
      // always select the snippet manually so we don't surface an error.
    }
  };
  return (
    <div className="flex items-start gap-3">
      <pre className="flex-1 bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre">
        {hint}
      </pre>
      <Button variant="ghost" type="button" onClick={onCopy} title="Copy snippet">
        <Copy className="h-4 w-4" />
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

interface ModelDetailProps {
  readonly detail: {
    readonly model: string;
    readonly tag: string;
    readonly digest: string;
    readonly size_bytes: number;
    readonly uploaded_at: string;
    readonly metadata: Record<string, unknown>;
    readonly pull_hint: string;
  };
}

function ModelDetail({ detail }: ModelDetailProps) {
  const meta = detail.metadata ?? {};
  const layers = (meta.layers as OllamaLayer[] | undefined) ?? [];
  const total = (meta.total_size as number | undefined) ?? 0;
  const configDigest = meta.config_digest as string | undefined;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">
            Manifest digest
          </dt>
          <dd className="font-mono text-xs text-fg break-all">{detail.digest}</dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Total size</dt>
          <dd className="tabular-nums text-fg">{bytes(total || detail.size_bytes)}</dd>
        </div>
        {configDigest && (
          <div>
            <dt className="text-fg-muted text-xs uppercase tracking-wide">
              Config digest
            </dt>
            <dd className="font-mono text-xs text-fg break-all">{configDigest}</dd>
          </div>
        )}
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Uploaded</dt>
          <dd className="text-fg-muted">
            {new Date(detail.uploaded_at).toLocaleString()}
          </dd>
        </div>
      </dl>

      <div>
        <h3 className="text-sm font-semibold text-fg mb-2">
          Layers ({layers.length})
        </h3>
        {layers.length === 0 ? (
          <p className="text-fg-muted text-sm">
            Manifest carries no parsed layers — likely an unrecognised media
            type or a malformed manifest.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="px-3 py-2 font-medium">Kind</th>
                  <th className="px-3 py-2 font-medium">Media type</th>
                  <th className="px-3 py-2 font-medium">Digest</th>
                  <th className="px-3 py-2 font-medium">Size</th>
                </tr>
              </thead>
              <tbody>
                {layers.map((l) => {
                  const { label, icon: Icon } = layerKindLabel(l.media_type);
                  return (
                    <tr key={l.digest} className="border-b border-border/50">
                      <td className="px-3 py-2 text-fg">
                        <span className="inline-flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-accent" />
                          {label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {l.media_type}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {shortHex(l.digest)}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-fg-muted">
                        {bytes(l.size)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PullSnippet hint={detail.pull_hint} />
    </div>
  );
}
